import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from "rxjs/operators";
import { Formation, CasingTubing, Well, WellStub, WellSearchSpec, WellSearchResult, WellWithFinancialAssurance, ReservedHoleNumbers, ReserveAPIRequest, OrphanWellRequest, GetWellsRequest, DataViewRequest, WellLogSearchSpec, WellProductionReporting, WellLog, ComplianceSearchSpec, Compliance, WellStimulation, WellStimulationSearchSpec, Inspection, InspectionSearchSpec, UpdateStratigraphicWellBondingRequirementRequest, StratigraphicWellBondingRequirement, WellComplianceData, WellsRequiringFA } from '../models/well.model';
import { DocumentWithLink } from "../models/document-tag.model";
import { BulkWellStatusChange, BulkWellStatusChangeSearchSpec } from "../../admin/models/bulk-well-status-change.model";

@Injectable()
export class WellService {


    constructor(
        public readonly httpClient: HttpClient
    ) { }

    getAPINumbers(havePermits: boolean): Observable<string[]> {
        return this.httpClient.get<string[]>(`/api/well/apiNumbers/${havePermits}`);
    }

    getReservedHoleNumbers(): Observable<ReservedHoleNumbers[]> {
        return this.httpClient.get<ReservedHoleNumbers[]>("/api/well/reserved-hole-numbers");
    }

    reserveAPINumber(api: ReserveAPIRequest): Observable<boolean> {
        return this.httpClient.post<boolean>("api/well/reserve-api", api);
    }

    saveOrphanWell(request: OrphanWellRequest): Observable<boolean> {
        return this.httpClient.post<boolean>("api/well/save-orphan-well", request);
    }

    getCurrentAndHistoricalWells(orgIDs: string[]) {
        let options = {
            params: {
                orgIDs: orgIDs
            }
        };

        return this.httpClient.get<Well[]>('/api/well/all', options)
    }

    getWellsForOrganizationWithFinancialAssuranceFlags(organization: string, historicOwnerNumber: string): Observable<WellWithFinancialAssurance[]> {
        if (historicOwnerNumber == null || historicOwnerNumber == undefined || historicOwnerNumber == "")
            return this.httpClient.get<WellWithFinancialAssurance[]>(`/api/well/withFAflags/${organization}`);
        else
            return this.httpClient.get<WellWithFinancialAssurance[]>(`/api/well/withFAflags/${organization}/${historicOwnerNumber}`);
    }

    getCurrentAndHistoricalWellStubs(orgIDs: string[]) {
        let options = {
            params: {
                orgIDs: orgIDs
            }
        };

        return this.httpClient.get<WellStub[]>('/api/well/org/stubs/all', options)
    }

    getWellById(id: string): Observable<Well> {
        return this.httpClient.get<Well>(`/api/well/${id}`);
    }

    getWellLogs(spec: WellLogSearchSpec): Observable<WellLog[]> {
        return this.httpClient.post<WellLog[]>('/api/well/get-well-logs', spec);
    }

    getTargetedWellData(wellIds: string[], requestType: DataViewRequest): Observable<any[]> {
        let request = {
            wellIds: wellIds,
            viewRequested: requestType
        } as GetWellsRequest;
        return this.httpClient.post<any[]>('/api/well/targeted-data', request);
    }

    getWellStubs(wellIds: string[]): Observable<WellStub[]> {
        let request = {
            wellIds: wellIds
        } as GetWellsRequest;
        return this.httpClient.post<WellStub[]>('/api/well/stubs', request);
    }

    getWells(wellIds: string[]): Observable<Well[]> {
        let options = {
            params: { ids: wellIds }
        };
        return this.httpClient.get<Well[]>('/api/well/wells', options);
    }

    getWellsStubsByOrganizationId(orgId: string) {
        return this.httpClient.get<WellStub[]>(`/api/well/org/stubs/${orgId}`);
    }

    getWellsByOrganizationId(orgId: string) {
        return this.httpClient.get<Well[]>(`/api/well/org/${orgId}`);
    }

    getWellsByReportType(orgIds: string[], reportType: string): Observable<Well[]> {
        let options = {
            params: {
                orgIds: orgIds,
                reportingType: reportType
            }
        };
        return this.httpClient.get<Well[]>('/api/issues/get-wells-by-report-type', options);
    }

    getWellDocuments(apiNumber: string): Observable<DocumentWithLink[]> {
        return this.httpClient.get<DocumentWithLink[]>(`/api/well/documents/${apiNumber}`)
    }

    getWellsPaged(searchSpec: PagedWellSearchSpec): Observable<{ item1: WellsRequiringFA[], item2: number }> {
        return this.httpClient.post<{ item1: WellsRequiringFA[], item2: number }>(`/api/well/paged/`, searchSpec);
    }

    saveWell(well: Well): Observable<any> {
        return this.httpClient.post(`/api/well/save`, well);
    }

    search(spec: WellSearchSpec): Observable<WellSearchResult[]> {
        return this.httpClient.post<WellSearchResult[]>("/api/well/search", spec);
	}

	searchProductionReporting(apiNumber: string): Observable<WellProductionReporting[]> {
		return this.httpClient.get<WellProductionReporting[]>(`/api/well/get-production-reporting/${apiNumber}`);
}

    saveBulkWellStatusChange(req: BulkWellStatusChange): Observable<BulkWellStatusChange> {
        return this.httpClient.post<BulkWellStatusChange>("/api/well/save-bulk-well-status-change", req);
    }

    searchBulkWellStatusChanges(spec: BulkWellStatusChangeSearchSpec): Observable<BulkWellStatusChange[]> {
        return this.httpClient.post<BulkWellStatusChange[]>("/api/well/search-bulk-well-status-changes", spec);
    }

    getCompliance(spec: ComplianceSearchSpec): Observable<Compliance[]> {
        return this.httpClient.post<Compliance[]>("/api/well/get-compliance", spec);
    }

    getStimulationRecords(spec: WellStimulationSearchSpec): Observable<WellStimulation[]> {
        return this.httpClient.post<WellStimulation[]>("/api/well/get-stimulation-records", spec);
    }

    getInspections(spec: InspectionSearchSpec): Observable<Inspection[]> {
        return this.httpClient.post<Inspection[]>("/api/well/get-inspections", spec);
	}

	saveCasingTubingData(data: CasingTubing[]): Observable<CasingTubing[]> {
		return this.httpClient.post<CasingTubing[]>("/api/well/save-casing-data", data);
	}

	saveFormationData(data: Formation[]): Observable<Formation[]> {
		return this.httpClient.post<Formation[]>("/api/well/save-formation-data", data);
    }

    saveWellComplianceData(data: WellComplianceData): Observable<WellComplianceData> {
        return this.httpClient.post<WellComplianceData>("/api/well/save-compliance-data", data);
    }

    updateStratigraphicWellBondingRequirements(req: UpdateStratigraphicWellBondingRequirementRequest): Observable<StratigraphicWellBondingRequirement[]> {
        return this.httpClient.post<StratigraphicWellBondingRequirement[]>("/api/well/update-stratigraphic-bonding-requirements", req);
	}

	deleteWell(api: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`/api/well/delete-api/${api}`);
}
}
export interface PagedWellSearchSpec {
    // Paging and ordering
    rowNumber?: number;
    perPage?: number;
    sortColumn?: string;
    sortDirection?: string;

  
    ordering?: OrderParameters;
    filters?: any;

    // Text columns with operator
    api_Number?: string;
    api_Number_Operator?: string;
    lease_Name?: string;
    lease_Name_Operator?: string;
    name?: string;
    name_Operator?: string;
    number?: string;
    number_Operator?: string;
    organizationName?: string;
    organizationName_Operator?: string;
    organizationId?: string;
    organizationId_Operator?: string;
    county_Name?: string;
    county_Name_Operator?: string;
    township_Name?: string;
    township_Name_Operator?: string;

    // Number columns with operator
    acresInDrillingUnit?: number;
    acresInDrillingUnit_Operator?: string;
    drillersMeasuredVerticalDepth?: number;
    drillersMeasuredVerticalDepth_Operator?: string;
    loggerMeasuredVerticalDepth?: number;
    loggerMeasuredVerticalDepth_Operator?: string;
    latitude83?: number;
    latitude83_Operator?: string;
    longitude83?: number;
    longitude83_Operator?: string;

    // Date columns with operator
    dateOfOriginalSpud?: string;
    dateOfOriginalSpud_Operator?: string;
    dateTotalDepthReached?: string;
    dateTotalDepthReached_Operator?: string;
    dateOfPlugBack?: string;
    dateOfPlugBack_Operator?: string;
    productionDateOfProduction?: string;
    productionDateOfProduction_Operator?: string;
    datePluggedAndAbandoned?: string;
    datePluggedAndAbandoned_Operator?: string;
    
    
    // Other columns
    wellType?: string;
    wellTypeDescription?: string;
    disposition?: string;
    legacyTracking?: string;
    tract?: boolean;
    releaseOfLeases?: boolean;
    permittedUrban?: boolean;
    isUrban?: boolean;
    isHorizontal?: boolean;
    slant?: string;
    PlugReportReceivedFromOperator?: boolean;
    h2SGasPresent?: boolean;
    proposedFormation?: string;
    deepestFormationPenetrated?: string;
    hasBuyerFA?: boolean;
}


export interface OrderParameters {
    field: string;
    ascending: boolean;
}

